import React, { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Mail, Phone, MapPin, Check, ShieldCheck, Sparkles, Clock, Map, Send, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { contactInfo } from '../data';
import ScrollReveal from '../components/ScrollReveal';

export default function ContactPage({ services = [] }) {
  const location = useLocation();
  
  // List of cities serviced in Østjylland
  const serviceAreas = [
    'Aarhus', 'Randers', 'Skanderborg', 'Horsens', 
    'Hinnerup', 'Hadsten', 'Galten', 'Hørning', 
    'Odder', 'Ry', 'Lystrup', 'Beder-Malling'
  ];

  const formRef = useRef(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    service: '',
    message: '',
    website_url: '' // Honeypot field
  });

  // Pre-fill service and message if passed from state
  useEffect(() => {
    if (location.state?.service) {
      setFormData(prev => ({
        ...prev,
        service: location.state.service,
        message: `Hej Morten, jeg vil gerne have et uforpligtende tilbud på: "${location.state.service}".`
      }));
    }
  }, [location.state]);

  const [status, setStatus] = useState({
    submitting: false,
    success: false,
    error: null
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ submitting: true, success: false, error: null });

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      const result = await response.json();

      if (response.ok && result.status === 'ok') {
        setStatus({
          submitting: false,
          success: true,
          error: null
        });
        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          address: '',
          service: '',
          message: '',
          website_url: ''
        });
      } else {
        throw new Error(result.message || 'Der skete en fejl under afsendelsen. Prøv venligst igen.');
      }
    } catch (err) {
      setStatus({
        submitting: false,
        success: false,
        error: err.message || 'Der kunne ikke oprettes forbindelse til serveren. Tjek din internetforbindelse.'
      });
    }
  };

  return (
    <div className="pt-24 sm:pt-28 lg:pt-32 pb-20 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Header Section */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 tracking-tight uppercase">
            KONTAKT MIG
          </h1>
          <p className="mt-4 text-lg text-slate-600">
            Har du spørgsmål, eller ønsker du at høre mere om min abonnementsordning? 
            Udfyld formularen nedenfor, eller kontakt mig direkte på telefon eller e-mail.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          
          {/* Left Column: Direct Contact Info & Area */}
          <div className="lg:col-span-5 space-y-6">
            <ScrollReveal delay={100}>
              <h2 className="text-xl font-bold text-slate-900 uppercase tracking-wider">Direkte kontakt</h2>
              
              {/* Contact Details Cards */}
              <div className="divide-y divide-slate-200 border-y border-slate-200">
                <a 
                  href={`tel:${contactInfo.phone.replace(/\s+/g, '')}`} 
                  className="flex items-center space-x-4 py-4 transition-colors group"
                >
                  <Phone className="w-5 h-5 text-slate-900 shrink-0" />
                  <div>
                    <p className="text-xs text-slate-500 font-semibold uppercase tracking-wider">Ring til Morten</p>
                    <p className="text-lg font-bold text-slate-900 group-hover:text-[#0e227b] transition-colors">{contactInfo.phone}</p>
                  </div>
                </a>

                <div className="flex items-center space-x-4 py-4">
                  <MapPin className="w-5 h-5 text-slate-900 shrink-0" />
                  <div>
                    <p className="text-xs text-slate-500 font-semibold uppercase tracking-wider">Adresse</p>
                    <p className="text-base font-bold text-slate-900">{contactInfo.address}</p>
                  </div>
                </div>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={200}>
              {/* Opening Hours */}
              <div className="bg-[#0e227b] text-white rounded-2xl p-6 space-y-4 shadow-lg">
                <div className="flex items-center space-x-2 border-b border-white/10 pb-3">
                  <Clock className="w-5 h-5 text-white/70" />
                  <h3 className="font-bold text-lg uppercase tracking-wider">Arbejdstider</h3>
                </div>
                <div className="space-y-3">
                  {contactInfo.hours.map((item, index) => (
                    <div key={index} className="flex justify-between text-sm">
                      <span className="text-white/70">{item.days}:</span>
                      <span className="font-semibold">{item.time}</span>
                    </div>
                  ))}
                </div>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={300}>
              {/* Guarantees Card */}
              <div className="bg-[#0e227b]/90 text-white rounded-3xl p-6 sm:p-8 shadow-md relative overflow-hidden">
                <div className="absolute -right-10 -bottom-10 opacity-10">
                  <ShieldCheck className="w-40 h-40" />
                </div>
                
                <h3 className="text-lg font-bold mb-4 flex items-center space-x-2 uppercase tracking-wider">
                  <ShieldCheck className="w-5 h-5 text-white/80" />
                  <span>Ravn's Tryghedsgaranti</span>
                </h3>
                
                <ul className="space-y-3 text-sm text-white/90">
                  <li className="flex items-start space-x-2">
                    <Check className="w-4 h-4 text-white/80 shrink-0 mt-1" />
                    <span><strong>100% Tilfredshedsgaranti:</strong> Er du ikke fuldt ud tilfreds, kommer jeg og gør det om uden beregning.</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <Check className="w-4 h-4 text-white/80 shrink-0 mt-1" />
                    <span><strong>Ansvarsforsikret:</strong> Jeg passer godt på dit hjem, men skulle uheldet være ude, er jeg fuldt forsikret.</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <Check className="w-4 h-4 text-white/80 shrink-0 mt-1" />
                    <span><strong>Miljøvenlige produkter:</strong> Jeg anvender udelukkende svanemærkede og biologisk nedbrydelige sæber.</span>
                  </li>
                </ul>
              </div>
            </ScrollReveal>
          </div>

          {/* Right Column: Contact Form & Service Area */}
          <div className="lg:col-span-7 space-y-6">
            
            <ScrollReveal delay={150}>
              {/* Contact Form Card */}
              <div className="bg-white rounded-3xl p-6 sm:p-8 border border-slate-100 shadow-sm space-y-6">
                <h2 className="text-xl font-bold text-slate-900 uppercase tracking-wider border-b border-slate-100 pb-4">
                  Send en besked
                </h2>

                {status.success ? (
                  <div className="text-center py-12 space-y-4">
                    <div className="w-16 h-16 bg-emerald-50 text-emerald-500 rounded-full flex items-center justify-center mx-auto">
                      <CheckCircle className="w-10 h-10" />
                    </div>
                    <h3 className="text-2xl font-bold text-slate-900 uppercase">Tak for din besked!</h3>
                    <p className="text-slate-600 text-sm max-w-md mx-auto">
                      Jeg har modtaget din forespørgsel og vender tilbage til dig med et uforpligtende tilbud eller svar hurtigst muligt.
                    </p>
                    <button
                      onClick={() => setStatus({ submitting: false, success: false, error: null })}
                      className="mt-6 bg-[#0e227b] hover:bg-[#0e227b]/90 text-white font-semibold px-6 py-2.5 rounded-full transition-colors cursor-pointer text-sm shadow-md"
                    >
                      Send en ny besked
                    </button>
                  </div>
                ) : (
                  <form ref={formRef} onSubmit={handleSubmit} className="space-y-4">
                    
                    {/* Antispam Honeypot Field - ABSOLUTELY HIDDEN */}
                    <div className="sr-only absolute -top-9999 -left-9999">
                      <label htmlFor="contact_website_url">Efterlad dette felt tomt</label>
                      <input
                        id="contact_website_url"
                        type="text"
                        name="website_url"
                        value={formData.website_url}
                        onChange={handleChange}
                        tabIndex="-1"
                        autoComplete="off"
                      />
                    </div>

                    {/* Name */}
                    <div className="space-y-1">
                      <label htmlFor="contact_name" className="block text-xs font-bold text-slate-700 uppercase tracking-wider">
                        Navn <span className="text-red-500">*</span>
                      </label>
                      <input
                        id="contact_name"
                        type="text"
                        name="name"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="Dit fulde navn"
                        className="w-full px-4 py-2.5 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-[#0e227b] focus:ring-2 focus:ring-[#0e227b]/10 outline-none transition-all text-sm text-slate-900 placeholder-slate-400"
                      />
                    </div>

                    {/* Contact Grid: Email & Phone */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-1">
                        <label htmlFor="contact_email" className="block text-xs font-bold text-slate-700 uppercase tracking-wider">
                          E-mail <span className="text-red-500">*</span>
                        </label>
                        <input
                          id="contact_email"
                          type="email"
                          name="email"
                          required
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="din@email.dk"
                          className="w-full px-4 py-2.5 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-[#0e227b] focus:ring-2 focus:ring-[#0e227b]/10 outline-none transition-all text-sm text-slate-900 placeholder-slate-400"
                        />
                      </div>

                      <div className="space-y-1">
                        <label htmlFor="contact_phone" className="block text-xs font-bold text-slate-700 uppercase tracking-wider">
                          Telefon <span className="text-red-500">*</span>
                        </label>
                        <input
                          id="contact_phone"
                          type="tel"
                          name="phone"
                          required
                          value={formData.phone}
                          onChange={handleChange}
                          placeholder="F.eks. 12345678"
                          className="w-full px-4 py-2.5 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-[#0e227b] focus:ring-2 focus:ring-[#0e227b]/10 outline-none transition-all text-sm text-slate-900 placeholder-slate-400"
                        />
                      </div>
                    </div>

                    {/* Address */}
                    <div className="space-y-1">
                      <label htmlFor="contact_address" className="block text-xs font-bold text-slate-700 uppercase tracking-wider">
                        Adresse / By <span className="text-red-500">*</span>
                      </label>
                      <input
                        id="contact_address"
                        type="text"
                        name="address"
                        required
                        value={formData.address}
                        onChange={handleChange}
                        placeholder="Vejnavn, husnummer, postnr. & by"
                        className="w-full px-4 py-2.5 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-[#0e227b] focus:ring-2 focus:ring-[#0e227b]/10 outline-none transition-all text-sm text-slate-900 placeholder-slate-400"
                      />
                    </div>



                    {/* Message */}
                    <div className="space-y-1">
                      <label htmlFor="contact_message" className="block text-xs font-bold text-slate-700 uppercase tracking-wider">
                        Besked / Detaljer <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        id="contact_message"
                        name="message"
                        required
                        rows="8"
                        value={formData.message}
                        onChange={handleChange}
                        placeholder="F.eks. antal vinduer, specielle adgangsforhold..."
                        className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-[#0e227b] focus:ring-2 focus:ring-[#0e227b]/10 outline-none transition-all text-sm text-slate-900 placeholder-slate-400 resize-none"
                      ></textarea>
                    </div>

                    {/* Error Message */}
                    {status.error && (
                      <div className="p-3 bg-rose-50 border border-rose-100 rounded-xl text-rose-600 text-xs flex items-start space-x-2">
                        <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                        <span>{status.error}</span>
                      </div>
                    )}

                    {/* Submit Button */}
                    <button
                      type="submit"
                      disabled={status.submitting}
                      className="w-full bg-[#0e227b] hover:bg-[#0e227b]/90 disabled:bg-slate-300 text-white font-bold py-3 px-6 rounded-full shadow-md hover:shadow-lg transition-all duration-200 flex items-center justify-center space-x-2 cursor-pointer text-sm"
                    >
                      {status.submitting ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin text-white" />
                          <span>Sender...</span>
                        </>
                      ) : (
                        <>
                          <Send className="w-4 h-4 text-white" />
                          <span>Send besked</span>
                        </>
                      )}
                    </button>

                    <p className="text-center text-[10px] text-slate-400 leading-tight">
                      Ved indsendelse accepterer du, at jeg kontakter dig angående din forespørgsel.
                    </p>
                  </form>
                )}
              </div>
            </ScrollReveal>
          </div>

        </div>

      </div>
    </div>
  );
}
